import React from 'react';
import tw from 'twin.macro';
import { css } from '@emotion/react';

import { withWebinarPlayerContext } from '$/components/WebinarPlayer/context';
import { HasJoined, PlayerMode } from '$/components/WebinarPlayer/types';

import useRunOnce from '$/hooks/useRunOnce';
import { RoomType } from '$/GraphQL/schema/schema';
import { useHasChangedShallow } from '$/hooks/useHasChanged';

import { VideoControlsBar } from './ControlBars';
import { TitleAndPresenters } from './TitleAndPresenters';
import { InteractionInfo } from '../../Interactions/InteractionInfo';
import { OverlayInteractionsParent } from './OverlayInteractionsParent';
import { PlayButtonWithReplayExpireTimer } from './PlayButtonWithReplayExpireTimer';
import { UnmuteButton } from './UnmuteButton';

interface Props {
  overlayItemsOverControls: InteractionInfo[];
}

type ControlVisibility = 'controls-none' | 'controls-ghosted' | 'controls-visible';

export const VideoStageControls: React.FC<Props> = withWebinarPlayerContext(
  [
    'currentRoom',
    'ewebinar',
    'mode',
    'dimensions',
    'interactionInEdit',
    'disableControlsHover',
    'isPlaying',
    'hasJoined',
    'error',
    'showOverlay',
    'mutedByJoiningIssue',
  ],
  'VideoStageControls'
)(({ overlayItemsOverControls, context }) => {
  const timeout = React.useRef<NodeJS.Timeout | null>(null);
  const {
    currentRoom,
    ewebinar,
    mode,
    dimensions,
    interactionInEdit,
    disableControlsHover,
    isPlaying,
    hasJoined,
    error,
    showOverlay,
    mutedByJoiningIssue,
  } = context;

  const [isGhosted, setGhosted] = React.useState(false);

  useRunOnce(() => {
    if (!isPlaying) return;

    // Show controls initially for 10s if in live session
    let timer: NodeJS.Timeout | undefined;

    timer = setTimeout(() => {
      setGhosted(true);
      timer = undefined;
    }, 10000);

    return (): void => {
      timer && clearTimeout(timer);
    };
  });

  React.useEffect(() => {
    // Cleanup timeout
    return (): void => {
      if (timeout.current) clearTimeout(timeout.current);
    };
  }, [timeout]);

  const hideControls = (): void => {
    setGhosted(true);
  };

  const showControlsWithTimeout = React.useCallback((): void => {
    if (disableControlsHover) {
      return;
    }
    setGhosted(false);

    timeout.current && clearTimeout(timeout.current);
    timeout.current = setTimeout((): void => {
      setGhosted(true);
    }, 4000);
  }, [disableControlsHover]);

  const hasJoinedChanged = useHasChangedShallow([hasJoined]);
  React.useEffect(() => {
    if (!hasJoinedChanged || hasJoined !== HasJoined.Joined || !isPlaying) return;

    // If attendee goes from not joined to joined start timer to time out controls
    showControlsWithTimeout();
  }, [hasJoined, hasJoinedChanged, isPlaying, showControlsWithTimeout]);

  let controlVisibilityClass: ControlVisibility =
    isGhosted && isPlaying ? 'controls-ghosted' : 'controls-visible';

  if (mode === PlayerMode.edit && !ewebinar?.vimeoVideoId) {
    // If in edit mode and currently uploading and no video already there then don't show play button
    controlVisibilityClass = 'controls-none';
  }

  const hasDragElementsEdit =
    interactionInEdit && interactionInEdit.hasVideoOverlay() === 'over-video';

  if (currentRoom.type !== RoomType.Presentation) {
    return null;
  }

  // A copy of this boolean is in Controls - if this changes also change that copy
  const showOverVideoError = error && hasJoined !== HasJoined.Joining;

  return (
    <div
      id='controlsCanvas'
      className={`
          absolute z-player-controls-canvas
          ${controlVisibilityClass}
          actions-${dimensions.actionsPane.location}
        `}
      css={css`
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        color: #fff;
        font-family: '${ewebinar?.font || 'unset'}';
        pointer-events: ${ewebinar.vimeoVideoId && !showOverVideoError ? 'auto' : 'none'};
        .control {
          color: #fff !important;
          transition: opacity 0.2s;
          opacity: 0;
          pointer-events: none !important;
        }

        &.controls-visible .control {
          opacity: 1;
          pointer-events: auto !important;
        }

        &:not(.controls-none) .controls-always-visible {
          opacity: 1;
          pointer-events: auto;
        }

        .controls-backdrop {
          ${tw`transition-opacity duration-200 z-player-controls-backdrop`}
          pointer-events: none;
          position: absolute;
          left: 0;
          right: 0;
          background: #000;
          opacity: 0;
        }

        &.controls-visible .controls-backdrop {
          opacity: 0.5;
        }

        &.controls-visible .expires-in-timer::before {
          opacity: 0.5;
        }
      `}
      onMouseMove={showControlsWithTimeout}
      onMouseOut={hideControls}
      onTouchStart={showControlsWithTimeout}
      onTouchMove={showControlsWithTimeout}
    >
      <TitleAndPresenters />

      {/* hide play button when we are showing it in the overlay */}
      {!showOverlay ? <PlayButtonWithReplayExpireTimer className={`play-control`} /> : null}
      {mutedByJoiningIssue ? <UnmuteButton className={`play-control`} /> : null}

      {dimensions.videoControlsArea.bottomBar.height === 0 && (
        <>
          <div
            // Mobile tap backdrop that covers only bottom bar controls
            className='controls-backdrop controls-bottom'
            css={css`
              bottom: 0;
              height: 33%;
              max-height: 25rem;
              opacity: 0.3;
              background: linear-gradient(0deg, #000 0%, #000 40%, #00000000 100%) !important;
            `}
          />

          <VideoControlsBar overVideo />
        </>
      )}

      {/** Overlay interactions that are interactive get rendered here and are clickable */}
      <OverlayInteractionsParent
        interactionInEdit={interactionInEdit}
        items={overlayItemsOverControls}
        className='z-player-clickable-overlay'
      />

      {/* -- This is used for editing -- */}
      {hasDragElementsEdit && (
        <div
          id='dragElementsEdit'
          className='overflow-hidden z-player-drag-edit'
          css={css`
            pointer-events: none;
            * {
              pointer-events: auto;
            }
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
          `}
        />
      )}
    </div>
  );
});
